import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import { formatTimeAgo } from '../../utils/format';

function ConversationList({ conversations, activeId, onSelect }) {
  return (
    <div className="flex flex-col">
      <div className="p-4 border-b border-[#E5E5E5]">
        <h2 className="font-bold text-[#1B1B1B] text-lg">Messages</h2>
      </div>
      <div className="overflow-y-auto flex-1">
        {conversations.length === 0 ? (
          <p className="text-center text-[#AAAAAA] py-8 text-sm">No conversations yet</p>
        ) : (
          conversations.map(conv => (
            <button
              key={conv.id}
              onClick={() => onSelect(conv)}
              className={`w-full flex items-center gap-3 p-4 hover:bg-[#F5F5F5] transition-colors border-b border-[#F0F0F0] text-left ${activeId === conv.id ? 'bg-[#F5F5F5] border-l-2 border-l-[#C8A800]' : ''}`}
            >
              {conv.other_avatar ? (
                <img src={conv.other_avatar} alt={conv.other_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0" style={{ backgroundColor: 'rgba(200,168,0,0.12)', color: '#C8A800' }}>
                  {conv.other_name?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-[#1B1B1B] text-sm truncate">{conv.other_name}</p>
                  {conv.last_message_at && <span className="text-xs text-[#BBBBBB] flex-shrink-0">{formatTimeAgo(conv.last_message_at)}</span>}
                </div>
                <p className="text-xs text-[#999999] truncate">{conv.last_message || 'Start a conversation'}</p>
              </div>
              {conv.unread_count > 0 && (
                <span className="w-5 h-5 bg-[#C8A800] text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                  {conv.unread_count}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function ChatWindow({ conversation, onBack }) {
  const { user } = useAuth();
  const { socket, clearConversationNotifications } = useSocket();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef(null);
  const typingTimeout = useRef(null);

  useEffect(() => {
    if (!conversation) return;
    loadMessages();
    clearConversationNotifications(conversation.id);

    if (socket) {
      socket.emit('join_conversation', conversation.id);

      socket.on('new_message', (msg) => {
        setMessages(prev => [...prev, msg]);
      });

      socket.on('user_typing', () => setTyping(true));
      socket.on('user_stop_typing', () => setTyping(false));

      return () => {
        socket.emit('leave_conversation', conversation.id);
        socket.off('new_message');
        socket.off('user_typing');
        socket.off('user_stop_typing');
      };
    }
  }, [conversation?.id, socket]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadMessages() {
    setLoading(true);
    try {
      const res = await api.get(`/messages/${conversation.id}`);
      setMessages(res.data);
    } catch {}
    setLoading(false);
  }

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim() || !socket) return;
    socket.emit('send_message', { conversationId: conversation.id, content: input.trim() });
    setInput('');
  };

  const handleTyping = (e) => {
    setInput(e.target.value);
    if (socket) {
      socket.emit('typing', { conversationId: conversation.id });
      clearTimeout(typingTimeout.current);
      typingTimeout.current = setTimeout(() => {
        socket.emit('stop_typing', { conversationId: conversation.id });
      }, 1500);
    }
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center flex-col gap-3 text-[#AAAAAA]">
        <svg className="w-10 h-10 text-[#DDDDDD]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        <p className="text-sm">Select a conversation</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-[#E5E5E5] bg-white">
        <button onClick={onBack} className="md:hidden text-[#AAAAAA] hover:text-[#1B1B1B] mr-1 transition-colors">←</button>
        {conversation.other_avatar ? (
          <img src={conversation.other_avatar} alt={conversation.other_name} className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: 'rgba(200,168,0,0.12)', color: '#C8A800' }}>
            {conversation.other_name?.[0]?.toUpperCase()}
          </div>
        )}
        <div>
          <p className="font-semibold text-[#1B1B1B]">{conversation.other_name}</p>
          <p className="text-xs text-[#999999] capitalize">{conversation.conversation_type} inquiry</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8F8F8]">
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          messages.map(msg => {
            const isMe = msg.sender_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                {!isMe && (
                  msg.sender_avatar ? (
                    <img src={msg.sender_avatar} alt="" className="w-7 h-7 rounded-full object-cover mr-2 flex-shrink-0 self-end" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#E5E5E5] flex items-center justify-center text-xs text-[#999999] mr-2 flex-shrink-0 self-end">
                      {msg.sender_name?.[0]}
                    </div>
                  )
                )}
                <div className={`max-w-xs lg:max-w-md xl:max-w-lg px-4 py-2.5 rounded-2xl ${
                  isMe ? 'bg-[#C8A800] text-white rounded-br-sm' : 'bg-white text-[#1B1B1B] rounded-bl-sm border border-[#E5E5E5]'
                }`}>
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-[#AAAAAA]'}`}>
                    {formatTimeAgo(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        {typing && (
          <div className="flex justify-start">
            <div className="bg-white border border-[#E5E5E5] px-4 py-2.5 rounded-2xl rounded-bl-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-[#CCCCCC] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-[#CCCCCC] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-[#CCCCCC] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={sendMessage}
        className="px-4 pt-4 border-t border-[#E5E5E5] flex gap-2 bg-white"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <input
          className="input flex-1 py-2.5"
          placeholder="Type a message..."
          value={input}
          onChange={handleTyping}
          style={{ fontSize: '16px' }}
        />
        <button type="submit" disabled={!input.trim()} className="btn-primary px-4 py-2.5 disabled:opacity-40">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </form>
    </div>
  );
}

export default function Messages() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showList, setShowList] = useState(true);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) { setActive(conv); setShowList(false); }
    }
  }, [conversationId, conversations]);

  async function fetchConversations() {
    try {
      const res = await api.get('/messages/conversations');
      setConversations(res.data);
    } catch {}
    setLoading(false);
  }

  const selectConversation = (conv) => {
    setActive(conv);
    setShowList(false);
    navigate(`/messages/${conv.id}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="page-container flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)] flex">
      {/* Sidebar */}
      <div className={`w-full md:w-80 border-r border-[#E5E5E5] flex flex-col bg-white ${!showList ? 'hidden md:flex' : 'flex'}`}>
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <ConversationList conversations={conversations} activeId={active?.id} onSelect={selectConversation} />
        )}
      </div>

      {/* Chat */}
      <div className={`flex-1 flex flex-col ${showList ? 'hidden md:flex' : 'flex'}`}>
        <ChatWindow conversation={active} onBack={() => setShowList(true)} />
      </div>
    </div>
  );
}
